import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { useAuth } from "@/context/AuthContext";
import { useT, LanguageSwitcher } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Coins, SignOut, BoxingGlove, PersonSimpleTaiChi, CalendarBlank, Trash, Copy, Gear } from "@phosphor-icons/react";
import { Logo } from "@/components/Logo";
import { TokenBanner } from "@/components/TokenBanner";

const TYPE_LABEL = { combat: "Combattimento", poomsae: "Poomsae", mixed: "Misto" };

export default function Dashboard() {
  const { user, logout, refresh } = useAuth();
  const t = useT();
  const nav = useNavigate();
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "combat", date: "", duration_days: 1 });
  const [loading, setLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const { data } = await api.get("/events");
    setEvents(data);
  };
  useEffect(() => {
    load();
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const create = async () => {
    if (!form.name) return toast.error(t("dash.enterName"));
    setLoading(true);
    try {
      const { data } = await api.post("/events", form);
      toast.success(t("dash.created"));
      setOpen(false);
      setForm({ name: "", type: "combat", date: "", duration_days: 1 });
      nav(`/events/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const askDelete = (ev, e) => { e.stopPropagation(); setConfirmText(""); setDeleteTarget(ev); };
  const confirmDelete = async () => {
    if (!deleteTarget || confirmText !== deleteTarget.name) return;
    setDeleting(true);
    try {
      await api.delete(`/events/${deleteTarget.id}`);
      toast.success(t("dash.deleted"));
      setDeleteTarget(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setDeleting(false);
    }
  };

  const StatusBadge = ({ status }) => {
    const map = {
      programmato: "bg-blue-500/15 text-blue-400 border-blue-500/30",
      in_corso: "bg-gold/15 text-gold border-gold/30",
      concluso: "bg-green-500/15 text-green-400 border-green-500/30",
      non_svolto: "bg-hong/15 text-hong border-hong/30",
    };
    const s = status || "programmato";
    return <span data-testid={`event-status-${s}`} className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-md border whitespace-nowrap ${map[s]}`}>{t(`status.${s}`)}</span>;
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold text-xl text-foreground">
            <Logo size={24} className="text-brand-yellow" /> <span>KIHAP</span>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <button onClick={() => nav("/billing")} className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-border bg-card hover:border-gold transition-colors" data-testid="token-balance">
              <Coins weight="fill" className="text-gold" size={18} />
              <span className="font-monot text-sm font-semibold tabular">{user?.tokens_balance ?? 0}</span>
              <span className="text-xs text-muted-foreground">{t("dash.token")} · +</span>
            </button>
            {user?.role === "admin" ? (
              <div className="hidden sm:flex items-center gap-3">
                <button onClick={() => nav("/admin")} className="text-sm font-semibold text-gold hover:underline" data-testid="admin-link">{t("admin.link")}</button>
                <button onClick={() => nav("/settings")} className="text-muted-foreground hover:text-foreground transition-colors" title={t("set.title")} data-testid="settings-link"><Gear size={20} /></button>
              </div>
            ) : (
              <div className="hidden sm:flex items-center gap-2">
                <span className="text-sm text-muted-foreground" data-testid="user-name">{user?.name || user?.email?.split("@")[0]}</span>
                <button onClick={() => nav("/settings")} className="text-muted-foreground hover:text-foreground transition-colors" title={t("set.title")} data-testid="settings-link"><Gear size={20} /></button>
              </div>
            )}
            <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-btn" title="Esci">
              <SignOut size={18} />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <TokenBanner />
        <div className="flex items-end justify-between mb-8">
          <div>
            <h1 className="text-4xl font-extrabold">{t("dash.events")}</h1>
            <p className="text-muted-foreground mt-1">{t("dash.eventsSub")}</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="font-semibold gap-2" data-testid="create-event-btn"><Plus weight="bold" /> {t("dash.newEvent")}</Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-border">
              <DialogHeader><DialogTitle>{t("dash.newEvent")}</DialogTitle></DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>{t("dash.eventName")}</Label>
                  <Input data-testid="event-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t("dash.namePlaceholder")} />
                </div>
                <div className="space-y-2">
                  <Label>{t("dash.type")}</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger data-testid="event-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="combat">{t("type.combat")}</SelectItem>
                      <SelectItem value="poomsae">{t("type.poomsae")}</SelectItem>
                      <SelectItem value="mixed">{t("type.mixed")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>{t("dash.date")}</Label>
                  <Input type="date" min={new Date().toISOString().split("T")[0]} data-testid="event-date-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
                </div>
                <div className="space-y-2">
                  <Label>{t("dash.duration")}</Label>
                  <Input type="number" min={1} data-testid="event-duration-input" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Math.max(1, parseInt(e.target.value) || 1) })} />
                </div>
              </div>
              <DialogFooter>
                <Button onClick={create} disabled={loading} data-testid="event-create-confirm">{loading ? t("dash.creating") || "…" : t("dash.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {events.length === 0 ? (
          <div className="border border-dashed border-border rounded-lg p-16 text-center text-muted-foreground" data-testid="events-empty">
            {t("dash.empty")}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="events-grid">
            {events.map((ev) => (
              <div key={ev.id} onClick={() => nav(`/events/${ev.id}`)}
                className="group border border-border rounded-lg p-6 bg-card hover:border-gold transition-colors cursor-pointer relative"
                data-testid={`event-card-${ev.id}`}>
                <div className="absolute top-4 right-4 flex items-center gap-2">
                  <StatusBadge status={ev.computed_status} />
                  <button onClick={(e) => askDelete(ev, e)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-hong transition-opacity" data-testid={`delete-event-${ev.id}`}>
                    <Trash size={18} />
                  </button>
                </div>
                <h3 className="text-2xl font-bold pr-28 -mt-3">{ev.name}</h3>
                {ev.date && <div className="text-sm text-muted-foreground flex items-center gap-1.5 mt-2"><CalendarBlank size={15} /> {ev.date}{ev.duration_days > 1 ? ` · ${ev.duration_days} ${t("dash.days")}` : ""}</div>}
                <div className="flex items-center gap-2 mt-4">
                  <div className="p-2 rounded-md bg-secondary">
                    {ev.type === "poomsae" ? (
                      <PersonSimpleTaiChi size={20} className="text-chung" />
                    ) : ev.type === "combat" ? (
                      <BoxingGlove size={20} className="text-hong" />
                    ) : (
                      <span className="flex items-center gap-1" data-testid="mixed-icon">
                        <BoxingGlove size={18} className="text-hong" />
                        <PersonSimpleTaiChi size={18} className="text-chung" />
                      </span>
                    )}
                  </div>
                  <Badge variant="secondary" className="uppercase text-[10px] tracking-widest">{t(`type.${ev.type}`)}</Badge>
                  {ev.my_role && ev.my_role !== "owner" && <Badge variant="secondary" className="uppercase text-[10px] tracking-widest text-brand-yellow" data-testid={`card-role-${ev.id}`}>{t(`collab.role${ev.my_role === "co_organizer" ? "Co" : ev.my_role === "registration" ? "Reg" : "Obs"}`)}</Badge>}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-card border-border" data-testid="delete-event-dialog">
          <DialogHeader><DialogTitle className="text-hong">{t("dash.deleteTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground whitespace-pre-line">{t("dash.deleteWarn")}</DialogDescription>
          {deleteTarget && (
            <button type="button" title={t("dash.clickToCopy")} data-testid="delete-name-hint"
              onClick={async () => { await copyToClipboard(deleteTarget.name); toast.success(t("ring.copied")); }}
              className="w-full flex items-center justify-between gap-2 text-left font-monot text-sm bg-secondary border border-border rounded-md px-3 py-2 hover:border-hong transition-colors">
              <span className="truncate">{deleteTarget.name}</span>
              <Copy size={16} className="shrink-0 text-muted-foreground" />
            </button>
          )}
          <Input autoFocus value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder={deleteTarget?.name} data-testid="delete-confirm-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDeleteTarget(null)} data-testid="delete-cancel">{t("dash.cancel")}</Button>
            <Button variant="destructive" disabled={deleting || confirmText !== deleteTarget?.name} onClick={confirmDelete} data-testid="delete-confirm-btn">
              {deleting ? "…" : t("dash.deleteCta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
